/*
  Project Structure, wave 3 (screen 08):
  - #1 a two-pane editor: on the left the tree (buildings › floors › panels / rooms) with counts, on the right the
       chosen building / floor / panel with its contents — no boxes inside boxes, no sticky rows. The tree keeps
       its place when you move between floors; on phones the tree and the details are two screens.
  - #6 address conflicts: the addresses that are really used twice are named in a red callout, their cells are
       marked "Duplicate", and the project's "Allow duplicates" switch lives here (it left the details table).
  Everything that changes the structure stays in the page (handlers, undo, save bar); this file draws it.
  Final QA: BMS projects use it too (`bms`): buildings › subs › sub-items with sub-items in the tree, the chosen
  one's sub-items as a grid on the right (BmsStructurePanels.jsx) — no more boxes in boxes / sticky rows.
*/
import { useMemo, useRef, useState } from 'react';
import {
  AlertTriangle, ChevronLeft, ChevronRight, CircleCheck, GripVertical, Layers, Pencil, Plus, Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { TableCheckbox } from '@/components/ui/table-checkbox';
import { RowActions } from '@/components/ui/row-actions';
import { StatusPill } from '@/components/ui/status-pill';
import { formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import { ActionMenu, MONO } from './ProjectUi';
import {
  StructureCountChip, StructureDeviceCells, StructureDeviceDoneButton, StructureKindIcon, StructureName,
  StructureRowActions, StructureSearch,
} from './StructurePanels';
import {
  changedContainerIds, getBuildingCollapseKey, getFloorCollapseKey, getPanelCollapseKey, getRoomCollapseKey,
} from './structureModel';
import { BmsBuildingBody, BmsNodeBody, bmsStructureNodes, bmsTypeField, countBmsItems } from './BmsStructurePanels';

const text = (value) => String(value ?? '').trim();
const list = (node, key) => (Array.isArray(node?.[key]) ? node[key] : []);
const fmt = (value) => formatNumber(value, { decimals: 0 });
const DEVICE_STEP = 50;

const isZone = (panel) => {
  const type = text(panel?.type).toLowerCase();
  return type ? type === 'zone' : text(panel?.name).toLowerCase().startsWith('zone');
};

const countDevices = (node) => {
  let total = list(node, 'devices').length;
  list(node, 'panels').forEach((panel) => { total += countDevices(panel); });
  list(node, 'rooms').forEach((room) => { total += countDevices(room); });
  list(node, 'floors').forEach((floor) => { total += countDevices(floor); });
  return total;
};

const KIND_WORD = { building: 'Building', floor: 'Floor', panel: 'Panel', zone: 'Zone', room: 'Room', roomPanel: 'Panel', sub: 'Sub', bmsNode: 'Sub-item' };
const IMPORT_LEVEL = { building: 'buildings', floor: 'floors', room: 'rooms', panel: 'panels', zone: 'panels', roomPanel: 'panels', sub: 'floors', bmsNode: 'devices' };
const HOLDS_DEVICES = new Set(['floor', 'panel', 'zone', 'room', 'roomPanel']);
const ICON_KIND = { roomPanel: 'panel', bmsNode: 'sub-item' };
const isBmsKind = (kind) => kind === 'sub' || kind === 'bmsNode';

/*
  BUG-010 (owner decision 2026-10-04): a building must have a name — the server refuses a structure save with a
  blank or whitespace-only building name (400, this exact text). The editor says so where it happens: renaming a
  building to nothing (Enter) shows it in the field, and a building that has no name is marked in the tree, its
  details and the overview (with "Name it"), so the save never fails without the place being shown.
*/
export const BUILDING_NAME_REQUIRED = 'Building name is required';
const isUnnamedBuilding = (entry) => entry?.kind === 'building' && !text(entry.node?.name);
/** The buildings of a structure that have no name (blank / whitespace) — what a save would be refused for. */
export const unnamedBuildings = (structure = {}) => list(structure, 'buildings').filter((building) => !text(building?.name));
/** Devices (KNX / LCS) or sub-items (BMS) below a tree entry. */
const countBelow = (entry, bms) => (bms ? countBmsItems(entry.node, entry.kind === 'building' ? 'building' : entry.kind === 'sub' ? 'sub' : 'item') : countDevices(entry.node));

/** Every container of the structure, parents first: { id, kind, node, name, location, parentId, path, depth, collapseKey }. */
export function structureNodes(structure = {}) {
  const nodes = [];
  list(structure, 'buildings').forEach((building) => {
    const bName = text(building.name) || 'Building';
    nodes.push({ id: building.id, kind: 'building', node: building, name: bName, location: { buildingId: building.id }, parentId: null, path: [], depth: 0, collapseKey: getBuildingCollapseKey(building.id) });
    list(building, 'floors').forEach((floor) => {
      const fName = text(floor.name) || 'Floor';
      const fLoc = { buildingId: building.id, floorId: floor.id };
      nodes.push({ id: floor.id, kind: 'floor', node: floor, name: fName, location: fLoc, parentId: building.id, path: [bName], depth: 1, collapseKey: getFloorCollapseKey(building.id, floor.id) });
      list(floor, 'panels').forEach((panel) => {
        const zone = isZone(panel);
        nodes.push({ id: panel.id, kind: zone ? 'zone' : 'panel', node: panel, name: text(panel.name) || (zone ? 'Zone' : 'Panel'), location: { ...fLoc, panelId: panel.id }, parentId: floor.id, path: [bName, fName], depth: 2, collapseKey: getPanelCollapseKey(building.id, floor.id, panel.id) });
      });
      list(floor, 'rooms').forEach((room) => {
        const rName = text(room.name) || 'Room';
        nodes.push({ id: room.id, kind: 'room', node: room, name: rName, location: { ...fLoc, roomId: room.id }, parentId: floor.id, path: [bName, fName], depth: 2, collapseKey: getRoomCollapseKey(building.id, floor.id, room.id) });
        list(room, 'panels').forEach((panel) => {
          nodes.push({ id: panel.id, kind: 'roomPanel', node: panel, name: text(panel.name) || 'Panel', location: { ...fLoc, roomId: room.id, panelId: panel.id }, parentId: room.id, path: [bName, fName, rName], depth: 3, collapseKey: getPanelCollapseKey(building.id, floor.id, room.id, panel.id) });
        });
      });
    });
  });
  return nodes;
}

const childrenOf = (entry, byParent) => byParent.get(entry.id) || [];

/** "1 panel · 8 rooms", "2 zones", "9 rooms" — what a floor / room really holds. */
export function mixLabel(kids = []) {
  const count = (kind) => kids.filter((kid) => kid.kind === kind).length;
  const part = (n, noun) => (n ? `${n} ${noun}${n === 1 ? '' : 's'}` : '');
  return [part(count('panel') + count('roomPanel'), 'panel'), part(count('zone'), 'zone'), part(count('room'), 'room')].filter(Boolean).join(' · ');
}

const TREE_KEYS = new Set(['ArrowDown', 'ArrowUp', 'Home', 'End']);

function TreeRow({ entry, byParent, selectedId, collapsed, onToggle, onSelect, ops, tabbableId, nav, dirtyIds, bms }) {
  const kids = childrenOf(entry, byParent);
  const open = kids.length > 0 && !(collapsed[entry.collapseKey] ?? true);
  const selected = entry.id === selectedId;
  const devices = countBelow(entry, bms);
  // drops: KNX devices onto floors / panels / rooms; BMS sub-items onto subs / sub-items, subs onto buildings
  const canDrop = ops.canEdit && (ops.canDropOn ? ops.canDropOn(entry) : Boolean(ops.dragging) && HOLDS_DEVICES.has(entry.kind));
  const draggableRow = Boolean(ops.canEdit && ops.canDragRow?.(entry));
  const menus = ops.rowMenus(entry);
  const labelText = `${KIND_WORD[entry.kind].toLowerCase()} ${entry.name}`.trim();
  return (
    <li role="none">
      <div
        role="treeitem"
        aria-selected={selected}
        aria-expanded={kids.length ? open : undefined}
        aria-level={entry.depth + 1}
        tabIndex={entry.id === tabbableId ? 0 : -1}
        data-structure-node={entry.id}
        onFocus={(event) => { if (event.target === event.currentTarget) nav.onFocusItem(entry.id); }}
        data-testid={`structure-tree-${entry.id}`}
        onClick={(event) => {
          if (event.target.closest('button, input, [role="menuitem"]')) return;
          if (!event.currentTarget.contains(event.target)) return;
          onSelect(entry);
        }}
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget) return;
          if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onSelect(entry); }
          if (event.key === 'F2' && ops.canEdit) { event.preventDefault(); ops.startRename(entry.id); }
          // tree keyboard pattern: ↓ ↑ Home End move, → opens / goes to the first child, ← closes / goes to the parent
          if (TREE_KEYS.has(event.key)) { event.preventDefault(); nav.move(entry.id, event.key); }
          if (event.key === 'ArrowRight' && kids.length) { event.preventDefault(); if (open) nav.focus(kids[0].id); else onToggle(entry.collapseKey); }
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            if (open) onToggle(entry.collapseKey);
            else if (entry.parentId) nav.focus(entry.parentId);
          }
        }}
        className={cn(
          'group/row relative flex min-h-9 cursor-pointer items-center gap-1.5 rounded-lg pr-1.5 text-[13.5px] outline-none transition-colors',
          'focus-visible:ring-2 focus-visible:ring-ring',
          selected ? 'bg-brand-soft font-semibold text-brand-ink before:absolute before:inset-y-1 before:left-0 before:w-[3px] before:rounded-full before:bg-brand' : 'hover:bg-surface-hover',
          ops.isImported(IMPORT_LEVEL[entry.kind], entry.id) && 'bg-status-amber-bg',
          ops.focusClass(entry.id)
        )}
        style={{ paddingLeft: 4 + entry.depth * 18 }}
        draggable={draggableRow || undefined}
        onDragStart={draggableRow ? (event) => { if (event.target === event.currentTarget) ops.dragRowStart(entry); } : undefined}
        onDragEnd={draggableRow ? ops.dragEnd : undefined}
        onDragOver={(event) => { if (canDrop) event.preventDefault(); }}
        onDrop={(event) => {
          if (!canDrop) return;
          event.preventDefault();
          ops.drop(entry, null);
        }}
      >
        {kids.length ? (
          <button
            type="button"
            onClick={() => onToggle(entry.collapseKey)}
            aria-label={`${open ? 'Collapse' : 'Expand'} ${entry.name}`}
            aria-expanded={open}
            tabIndex={-1}
            className="grid h-6 w-6 shrink-0 place-items-center rounded-md text-ink-3 hover:bg-surface-hover hover:text-ink"
          >
            <ChevronRight className={cn('h-4 w-4 transition-transform', open && 'rotate-90')} aria-hidden="true" />
          </button>
        ) : <span className="w-6 shrink-0" aria-hidden="true" />}
        <StructureKindIcon kind={ICON_KIND[entry.kind] || entry.kind} className={selected ? 'text-brand-ink' : undefined} />
        <StructureName
          value={entry.node.name}
          nodeId={entry.id}
          kind={KIND_WORD[entry.kind]}
          editing={ops.renamingId === entry.id}
          canEdit={ops.canEdit}
          onStartEdit={() => ops.startRename(entry.id)}
          onCommit={(name) => ops.commitName(entry, name)}
          onCancel={ops.stopRename}
          requiredMessage={entry.kind === 'building' ? BUILDING_NAME_REQUIRED : ''}
          className={cn('flex-1 font-medium', selected && 'font-semibold text-brand-ink', entry.depth === 0 && 'font-semibold')}
          fieldClassName="flex-1"
          inputClassName="h-7 w-full min-w-0"
        />
        {isUnnamedBuilding(entry) && ops.renamingId !== entry.id ? (
          <span className="shrink-0 rounded-full bg-status-red-bg px-1.5 py-0.5 text-[11px] font-semibold text-status-red-ink" title={BUILDING_NAME_REQUIRED} data-testid={`structure-tree-unnamed-${entry.id}`}>
            Name required
          </span>
        ) : null}
        {dirtyIds?.has(entry.id) ? (
          <span className="ui-tone-dot tone-amber h-2 w-2 shrink-0" title="Unsaved changes in here" data-testid={`structure-tree-dirty-${entry.id}`}>
            <span className="sr-only">Unsaved changes in here</span>
          </span>
        ) : null}
        <span className={cn('ml-auto flex shrink-0 items-center gap-1', ops.canEdit && '[@media(hover:hover)]:group-hover/row:hidden [@media(hover:hover)]:group-focus-within/row:hidden')}>
          <StructureCountChip
            label={entry.name}
            floors={entry.kind === 'building' ? list(entry.node, 'floors').length : undefined}
            childNoun={bms ? 'sub' : 'floor'}
            devices={devices}
            interactive={false}
            testId={`structure-count-${entry.kind === 'roomPanel' || entry.kind === 'zone' ? 'panel' : entry.kind}-${entry.id}`}
          />
        </span>
        {ops.canEdit ? (
          <span className="hidden shrink-0 [@media(hover:hover)]:group-hover/row:flex [@media(hover:hover)]:group-focus-within/row:flex [@media(hover:none)]:flex">
            <StructureRowActions
              label={labelText}
              testId={`structure-actions-${entry.kind === 'roomPanel' || entry.kind === 'zone' ? 'panel' : entry.kind}-${entry.id}`}
              onRename={() => ops.startRename(entry.id)}
              addItems={menus.addItems}
              menuItems={menus.menuItems}
              className="gap-0.5 opacity-100 [@media(hover:hover)]:opacity-100"
              compact
            />
          </span>
        ) : null}
      </div>
      {open ? (
        <ul role="group" className="mt-0.5 space-y-0.5">
          {kids.map((child) => (
            <TreeRow key={child.id} entry={child} byParent={byParent} selectedId={selectedId} collapsed={collapsed} onToggle={onToggle} onSelect={onSelect} ops={ops} tabbableId={tabbableId} nav={nav} dirtyIds={dirtyIds} bms={bms} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

/* ------------------------------------------------------------------ right pane */

function ConflictCallout({ entry, conflicts, allowDuplicates, onAllowChange, saving, canEdit, onJump }) {
  // conflicts: [{ address, entries: [{ id, name, parentId, path }] }]
  const here = entry ? conflicts.filter((conflict) => conflict.entries.some((item) => item.parentId === entry.id)) : [];
  const elsewhere = conflicts.length - here.length;
  const hasAny = conflicts.length > 0;
  const tone = !hasAny ? 'ok' : allowDuplicates ? 'amber' : 'red';
  // 28 #8: the rule is PUT /projects/{id} (Projects + Project Structure edit) — a role that may not change it reads it
  const editable = canEdit && Boolean(onAllowChange);
  const toggle = editable ? (
    <label className="flex shrink-0 items-center gap-2 text-[12.5px] font-medium text-ink-2">
      <Switch
        checked={allowDuplicates}
        onCheckedChange={onAllowChange}
        disabled={saving}
        aria-label="Allow duplicate device addresses in this project"
        data-testid="structure-allow-duplicate-device-addresses"
      />
      Allow duplicates in this project
    </label>
  ) : (
    <span className="shrink-0 text-[12.5px] font-medium text-ink-3" data-testid="structure-duplicate-rule">
      {allowDuplicates ? 'Duplicates allowed in this project' : 'Duplicates not allowed in this project'}
    </span>
  );
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border px-4 py-3',
        tone === 'red' && 'border-[color:var(--danger-border,#f3c2c2)] bg-status-red-bg',
        tone === 'amber' && 'border-[color:var(--warning-border,#f5d9a8)] bg-status-amber-bg',
        tone === 'ok' && 'border-line bg-surface-2'
      )}
      role={tone === 'red' ? 'alert' : 'status'}
      data-testid="structure-conflicts"
    >
      {tone === 'ok' ? <CircleCheck className="h-4 w-4 shrink-0 text-status-green-ink" aria-hidden="true" /> : <AlertTriangle className={cn('h-4 w-4 shrink-0', tone === 'red' ? 'text-status-red-ink' : 'text-status-amber-ink')} aria-hidden="true" />}
      <div className="min-w-[220px] flex-1 text-[13px] leading-snug">
        {!hasAny ? (
          <span className="text-ink-2">No device address is used twice in this project.</span>
        ) : here.length ? (
          here.slice(0, 2).map((conflict) => (
            <p key={conflict.address} className={tone === 'red' ? 'text-status-red-ink' : 'text-status-amber-ink'}>
              <b className="font-semibold">Address <span className={MONO}>{conflict.address}</span> is used {conflict.entries.length === 2 ? 'twice' : `${conflict.entries.length} times`}{conflict.entries.every((item) => item.parentId === entry.id) ? ' on this ' + (KIND_WORD[entry.kind] || 'list').toLowerCase() : ''}</b>
              <span className="text-ink-2"> — {conflict.entries.map((item) => item.name).join(' and ')}.</span>
            </p>
          ))
        ) : (
          <p className={tone === 'red' ? 'text-status-red-ink' : 'text-status-amber-ink'}>
            <b className="font-semibold">{conflicts.length} address{conflicts.length === 1 ? ' is' : 'es are'} used more than once in this project</b>
            <span className="text-ink-2"> — </span>
            {conflicts.slice(0, 3).map((conflict, index) => (
              <span key={conflict.address}>
                {index ? ', ' : ''}
                <button type="button" className={cn(MONO, 'font-semibold underline-offset-2 hover:underline')} onClick={() => onJump(conflict.entries[0])}>{conflict.address}</button>
                <span className="text-ink-3"> ({conflict.entries[0].path.slice(0, 2).join(' › ')})</span>
              </span>
            ))}
            {conflicts.length > 3 ? <span className="text-ink-3"> and {conflicts.length - 3} more</span> : null}
          </p>
        )}
        {here.length && elsewhere > 0 ? <p className="mt-0.5 text-[12px] text-ink-3">{elsewhere} more elsewhere in the project.</p> : null}
        {hasAny && !allowDuplicates ? <p className="mt-0.5 text-[12px] text-ink-3">Saving is refused while they repeat{editable ? ' — fix one of them, or allow duplicates' : ''}.</p> : null}
      </div>
      {toggle}
    </div>
  );
}

function DeviceTable({ entry, ops, duplicateIds }) {
  const devices = ops.devicesOf(entry);
  const [limit, setLimit] = useState(DEVICE_STEP);
  const canEdit = ops.canEdit;
  // 28 #8: selecting devices only feeds bulk Delete, and the grip only drags: a View-only role gets neither
  const selectable = canEdit && ops.selectable(entry);
  const selected = selectable ? ops.selectedOf(entry) : [];
  if (!devices.length) {
    return (
      <div className="rounded-xl border border-dashed border-[color:var(--line-dashed)] px-4 py-6 text-center text-[13px] text-ink-3">
        No devices here yet.
        {canEdit ? (
          <Button type="button" variant="outline" size="sm" className="ml-3" onClick={() => ops.addDevices(entry)}>
            <Plus aria-hidden="true" />
            Add devices
          </Button>
        ) : null}
      </div>
    );
  }
  const shown = devices.slice(0, limit);
  // 08 #6: a fixed last column — a "Duplicate" pill or "Delete 3…" no longer widens one row's tracks.
  const gridCols = !canEdit
    ? 'md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.4fr)_minmax(0,0.8fr)_8.5rem]'
    : selectable ? 'md:grid-cols-[20px_28px_minmax(0,0.9fr)_minmax(0,1.4fr)_minmax(0,0.8fr)_8.5rem]' : 'md:grid-cols-[28px_minmax(0,0.9fr)_minmax(0,1.4fr)_minmax(0,0.8fr)_8.5rem]';
  // Phones: a compact two-line card — name and ⋯ on top, part no · address (mono) under it.
  const phoneGrid = cn(
    'max-md:gap-x-2 max-md:gap-y-0 max-md:py-1.5',
    selectable
      ? "max-md:grid-cols-[20px_auto_minmax(0,1fr)_auto] max-md:[grid-template-areas:'chk_name_name_act'_'chk_part_addr_act']"
      : "max-md:grid-cols-[auto_minmax(0,1fr)_auto] max-md:[grid-template-areas:'name_name_act'_'part_addr_act']",
    'max-md:[&_[data-device-cell=name]]:[grid-area:name] max-md:[&_[data-device-cell=part_number]]:[grid-area:part] max-md:[&_[data-device-cell=address]]:[grid-area:addr]',
    'max-md:[&_[data-device-cell=part_number]]:h-6 max-md:[&_[data-device-cell=address]]:h-6',
    'max-md:[&_[data-device-cell=part_number]]:text-ink-3 max-md:[&_[data-device-cell=address]]:text-ink-3',
    'max-md:[&_[data-device-cell=name]]:font-semibold'
  );
  return (
    <div className="overflow-hidden rounded-xl border border-line" data-testid={`structure-devices-${entry.id}`}>
      <div className={cn('hidden items-center gap-2 border-b border-line bg-surface-2 px-3 py-2 text-[11.5px] font-bold text-ink-3 md:grid', gridCols)}>
        {selectable ? (
          <TableCheckbox
            checked={selected.length > 0 && selected.length === devices.length}
            indeterminate={selected.length > 0 && selected.length < devices.length}
            onCheckedChange={(checked) => ops.toggleAll(entry, Boolean(checked))}
            aria-label={`Select all devices in ${entry.name}`}
          />
        ) : null}
        {canEdit ? <span /> : null}
        <span>Part no</span>
        <span>Device</span>
        <span>Address</span>
        <span className="flex justify-end">
          {selected.length ? (
            <Button type="button" size="xs" variant="danger" onClick={() => ops.deleteSelected(entry)}>
              <Trash2 aria-hidden="true" />
              Delete {selected.length}…
            </Button>
          ) : null}
        </span>
      </div>
      <div
        className="divide-y divide-line-2"
        onDragOver={(event) => { if (canEdit && ops.dragging) event.preventDefault(); }}
        onDrop={(event) => { if (!canEdit || !ops.dragging) return; event.preventDefault(); ops.drop(entry, null); }}
      >
        {shown.map((device) => {
          const editing = ops.editingDevice?.id === device.id;
          const duplicate = duplicateIds.has(device.id);
          return (
            <div
              key={device.id}
              data-structure-node={device.id}
              tabIndex={-1}
              className={cn(
                'group/row grid items-center gap-2 px-3 py-1 outline-none',
                gridCols,
                phoneGrid,
                duplicate ? 'bg-status-red-bg' : 'hover:bg-surface-hover',
                ops.dragging?.deviceId === device.id && 'opacity-40',
                ops.isImported('devices', device.id) && 'bg-status-amber-bg',
                editing && 'bg-surface-2',
                ops.focusClass(device.id)
              )}
              onDragOver={(event) => { if (canEdit && ops.dragging) event.preventDefault(); }}
              onDrop={(event) => { if (!canEdit || !ops.dragging) return; event.preventDefault(); event.stopPropagation(); ops.drop(entry, device.id); }}
            >
              {selectable ? (
                <TableCheckbox
                  checked={selected.includes(device.id)}
                  onCheckedChange={(checked) => ops.toggleDevice(entry, device.id, Boolean(checked))}
                  aria-label={`Select ${device.name || device.part_number || 'device'}`}
                  className="max-md:[grid-area:chk]"
                />
              ) : null}
              {canEdit ? (
                <button
                  type="button"
                  draggable
                  onDragStart={() => ops.dragStart(entry, device.id)}
                  onDragEnd={ops.dragEnd}
                  // touch screens cannot drag: no handle there (and none in the phone card)
                  className="grid h-7 w-7 place-items-center rounded-md text-ink-4 opacity-60 hover:bg-surface-hover hover:text-ink-2 group-hover/row:opacity-100 max-md:hidden [@media(hover:none)]:invisible"
                  title="Drag to reorder, or onto a panel in the tree to move it"
                  aria-label={`Drag ${device.name || 'device'}`}
                >
                  <GripVertical className="h-4 w-4" />
                </button>
              ) : null}
              <StructureDeviceCells
                device={device}
                editing={editing}
                focusField={ops.editingDevice?.field}
                canEdit={canEdit}
                onStartEdit={(field) => ops.setEditingDevice({ id: device.id, field })}
                onChange={(field, value) => ops.deviceChange(entry, device.id, field, value)}
                onDone={() => ops.setEditingDevice(null)}
                duplicateAddress={duplicate}
              />
              <span className="flex items-center justify-end gap-1.5 max-md:[grid-area:act]">
                {duplicate && !editing ? <StatusPill tone="red" label="Duplicate" size="sm" /> : null}
                {editing ? <StructureDeviceDoneButton onDone={() => ops.setEditingDevice(null)} /> : (canEdit ? (
                  <RowActions
                    label={`Actions for device ${device.name || device.part_number || ''}`.trim()}
                    className="opacity-60 group-hover/row:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
                    items={[
                      { label: 'Edit device', icon: Pencil, onSelect: () => ops.setEditingDevice({ id: device.id, field: 'part_number' }) },
                      { label: 'Delete device…', icon: Trash2, danger: true, onSelect: () => ops.deleteDevice(entry, device.id) },
                    ]}
                  />
                ) : null)}
              </span>
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between gap-2 border-t border-line bg-surface-2 px-3 py-2 text-[12.5px] text-ink-3">
        <span>Showing {fmt(shown.length)} of {fmt(devices.length)} device{devices.length === 1 ? '' : 's'}</span>
        {devices.length > limit ? (
          <button type="button" className="font-semibold text-brand-ink hover:underline" onClick={() => setLimit(devices.length)}>Show all</button>
        ) : null}
      </div>
    </div>
  );
}

function ChildList({ title, items, onSelect, emptyText, bms = false }) {
  return (
    <section className="space-y-2">
      <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">{title}</p>
      {items.length ? (
        <ul className="divide-y divide-line-2 overflow-hidden rounded-xl border border-line">
          {items.map((child) => {
            const devices = countBelow(child, bms);
            const unit = 'device';
            const count = (key, noun) => {
              const n = list(child.node, key).length;
              return n ? `${n} ${noun}${n === 1 ? '' : 's'}` : '';
            };
            const sub = child.kind === 'building'
              ? count('floors', bms ? 'sub' : 'floor') || (bms ? 'No subs yet' : 'No floors yet')
              : child.kind === 'floor'
                ? [count('panels', 'panel'), count('rooms', 'room')].filter(Boolean).join(' · ') || 'Empty floor'
                : child.kind === 'room' ? (count('panels', 'panel') || 'Room') : KIND_WORD[child.kind];
            return (
              <li key={child.id}>
                <button type="button" onClick={() => onSelect(child)} className="flex min-h-11 w-full items-center gap-3 px-3 py-2 text-left hover:bg-surface-hover" data-testid={`structure-open-${child.id}`}>
                  <StructureKindIcon kind={ICON_KIND[child.kind] || child.kind} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13.5px] font-semibold text-ink">{child.name}</span>
                    <span className="text-[12px] text-ink-3">{sub}</span>
                  </span>
                  <span className="text-[12.5px] tabular-nums text-ink-2">{fmt(devices)} {unit}{devices === 1 ? '' : 's'}</span>
                  <ChevronRight className="h-4 w-4 text-ink-4" aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ul>
      ) : <p className="rounded-xl border border-dashed border-[color:var(--line-dashed)] px-4 py-4 text-[13px] text-ink-3">{emptyText}</p>}
    </section>
  );
}

function Detail({ entry, byParent, byId, ops, conflicts, duplicateIds, onSelect, onBack, isPhone, allowDuplicates, onAllowChange, duplicateSaving, bms }) {
  const kids = childrenOf(entry, byParent);
  const menus = ops.rowMenus(entry);
  const ancestors = [];
  let current = entry.parentId ? byId.get(entry.parentId) : null;
  while (current) {
    ancestors.unshift(current);
    current = current.parentId ? byId.get(current.parentId) : null;
  }
  const devices = countBelow(entry, bms);
  const hasOwnDevices = ['floor', 'panel', 'zone', 'room', 'roomPanel'].includes(entry.kind);
  const typeLabel = isBmsKind(entry.kind) ? String(entry.node[bmsTypeField(entry.kind)] || '').trim() : '';
  return (
    <div className="space-y-4" data-testid="structure-detail">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <nav aria-label="Where this is" className="flex flex-wrap items-center gap-1 text-[12.5px] text-ink-3">
            {isPhone ? (
              <button type="button" onClick={onBack} className="-ml-1 mr-1 inline-flex items-center gap-0.5 font-semibold text-brand-ink">
                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                Tree
              </button>
            ) : null}
            {ancestors.map((ancestor) => (
              <span key={ancestor.id} className="inline-flex items-center gap-1">
                <button type="button" className="hover:text-ink hover:underline" onClick={() => onSelect(ancestor)}>{ancestor.name}</button>
                <ChevronRight className="h-3.5 w-3.5 text-ink-4" aria-hidden="true" />
              </span>
            ))}
          </nav>
          <div className="mt-0.5 flex flex-wrap items-center gap-2">
            {isPhone && ops.renamingId === entry.id ? (
              // on phones the tree is hidden while a node is open: rename here
              <StructureName
                value={entry.node.name}
                nodeId={`${entry.id}-detail`}
                kind={KIND_WORD[entry.kind]}
                editing
                canEdit={ops.canEdit}
                onCommit={(name) => ops.commitName(entry, name)}
                onCancel={ops.stopRename}
                requiredMessage={entry.kind === 'building' ? BUILDING_NAME_REQUIRED : ''}
              />
            ) : isUnnamedBuilding(entry) ? (
              <h3 className="font-heading text-[20px] font-extrabold tracking-[-0.01em] text-ink-3">Untitled building</h3>
            ) : (
              <h3 className="font-heading text-[20px] font-extrabold tracking-[-0.01em] text-ink">{entry.name}</h3>
            )}
            <StatusPill tone="gray" label={typeLabel ? `${KIND_WORD[entry.kind]} · ${typeLabel}` : KIND_WORD[entry.kind]} size="sm" hideDot />
            {ops.canEdit ? (
              <Button type="button" variant="ghost" size="sm" onClick={() => ops.startRename(entry.id)} aria-label={`Rename ${entry.name} (in the tree)`}>
                <Pencil aria-hidden="true" />
                Rename
              </Button>
            ) : null}
          </div>
          {isUnnamedBuilding(entry) ? (
            <p className="mt-1 flex items-center gap-1 text-[12.5px] font-semibold text-status-red-ink" role="alert" data-testid="structure-detail-name-required">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {BUILDING_NAME_REQUIRED}{ops.canEdit ? ' — rename it before saving the structure.' : '.'}
            </p>
          ) : null}
          <p className="mt-1 flex flex-wrap items-center gap-x-3 text-[12.5px] text-ink-3">
            <span>{fmt(devices)} device{devices === 1 ? '' : 's'}{bms && entry.kind !== 'building' ? ' in all' : ''}</span>
            {entry.kind === 'building' ? <span>{list(entry.node, 'floors').length} {bms ? 'sub' : 'floor'}{list(entry.node, 'floors').length === 1 ? '' : 's'}</span> : null}
            {kids.length && entry.kind !== 'building' && !bms ? <span>{mixLabel(kids)}</span> : null}
          </p>
        </div>
        {ops.canEdit ? (
          <div className="flex items-center gap-2">
            {menus.addItems?.length ? (
              <ActionMenu
                label={`Add to ${entry.name}`}
                align="end"
                trigger={(
                  <Button type="button" variant="outline" size="sm" data-testid="structure-detail-add">
                    <Plus aria-hidden="true" />
                    Add
                    <ChevronRight className="rotate-90" aria-hidden="true" />
                  </Button>
                )}
                items={menus.addItems}
              />
            ) : null}
            {menus.menuItems?.length ? <RowActions label={`More for ${entry.name}`} items={menus.menuItems} /> : null}
          </div>
        ) : null}
      </div>

      <ConflictCallout
        entry={entry}
        conflicts={conflicts}
        allowDuplicates={allowDuplicates}
        onAllowChange={onAllowChange}
        saving={duplicateSaving}
        canEdit={ops.canEdit}
        onJump={(item) => ops.jump(item)}
      />

      {bms && entry.kind === 'building' ? (
        <BmsBuildingBody entry={entry} kids={kids} onSelect={onSelect} canEdit={ops.canEdit} onAddSub={() => ops.bms.add(entry)} />
      ) : null}
      {bms && isBmsKind(entry.kind) ? (
        <BmsNodeBody entry={entry} ops={ops} onOpen={(nodeId) => { const target = byId.get(nodeId); if (target) onSelect(target); }} openIds={new Set(kids.map((kid) => kid.id))} />
      ) : null}
      {!bms && entry.kind === 'building' ? (
        <ChildList title={`Floors · ${kids.length}`} items={kids} onSelect={onSelect} emptyText="No floors yet — use Add › Floor." />
      ) : null}
      {(entry.kind === 'floor' || entry.kind === 'room') && kids.length ? (
        <ChildList title={entry.kind === 'floor' ? `Panels, zones and rooms · ${kids.length}` : `Panels · ${kids.length}`} items={kids} onSelect={onSelect} />
      ) : null}
      {hasOwnDevices ? (
        <section className="space-y-2">
          {entry.kind === 'floor' || entry.kind === 'room' ? (
            <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">{entry.kind === 'floor' ? 'Floor devices' : 'Room devices'}</p>
          ) : null}
          {(entry.kind === 'floor' || entry.kind === 'room') && !ops.devicesOf(entry).length && kids.length ? (
            <p className="text-[12.5px] text-ink-3">None directly on this {KIND_WORD[entry.kind].toLowerCase()} — the devices sit in its panels.</p>
          ) : (
            <DeviceTable key={entry.id} entry={entry} ops={ops} duplicateIds={duplicateIds} />
          )}
        </section>
      ) : null}
    </div>
  );
}

function Overview({ nodes, conflicts, ops, allowDuplicates, onAllowChange, duplicateSaving, onSelect, onAddBuilding, bms }) {
  const buildings = nodes.filter((entry) => entry.kind === 'building');
  const devices = buildings.reduce((sum, entry) => sum + countBelow(entry, bms), 0);
  const unit = 'device';
  const unnamed = buildings.filter(isUnnamedBuilding);
  return (
    <div className="space-y-4" data-testid="structure-overview">
      <div>
        <h3 className="font-heading text-[20px] font-extrabold text-ink">The whole project</h3>
        <p className="mt-1 text-[12.5px] text-ink-3">
          {fmt(buildings.length)} building{buildings.length === 1 ? '' : 's'} · {fmt(devices)} {unit}{devices === 1 ? '' : 's'} · pick one to see {ops.canEdit ? 'and edit ' : ''}what is in it
        </p>
      </div>
      {unnamed.length ? (
        <div
          className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-[color:var(--danger-border,#f3c2c2)] bg-status-red-bg px-4 py-3"
          role="alert"
          data-testid="structure-unnamed-buildings"
        >
          <AlertTriangle className="h-4 w-4 shrink-0 text-status-red-ink" aria-hidden="true" />
          <div className="min-w-[220px] flex-1 text-[13px] leading-snug">
            <p className="font-semibold text-status-red-ink">{BUILDING_NAME_REQUIRED}</p>
            <p className="text-ink-2">
              {unnamed.length === 1 ? '1 building has' : `${fmt(unnamed.length)} buildings have`} no name
              {ops.canEdit ? ' — the structure cannot be saved until each one has a name.' : '.'}
            </p>
          </div>
          {ops.canEdit ? (
            <div className="flex flex-wrap gap-2">
              {unnamed.slice(0, 3).map((entry) => (
                <Button
                  key={entry.id}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => { onSelect(entry); ops.startRename(entry.id); }}
                  data-testid={`structure-name-building-${entry.id}`}
                >
                  <Pencil aria-hidden="true" />
                  {unnamed.length === 1 ? 'Name it' : `Name building ${buildings.indexOf(entry) + 1}`}
                </Button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
      <ConflictCallout entry={null} conflicts={conflicts} allowDuplicates={allowDuplicates} onAllowChange={onAllowChange} saving={duplicateSaving} canEdit={ops.canEdit} onJump={(item) => ops.jump(item)} />
      <ChildList
        title={`Buildings · ${buildings.length}`}
        items={buildings}
        bms={bms}
        onSelect={onSelect}
        emptyText={ops.canEdit ? 'No buildings yet — Add building, or Import a file.' : 'No buildings yet.'}
      />
      {ops.canEdit && onAddBuilding && buildings.length ? (
        <Button type="button" variant="outline" size="sm" onClick={onAddBuilding}>
          <Plus aria-hidden="true" />
          Add building
        </Button>
      ) : null}
    </div>
  );
}

/**
 * The two-pane editor. `ops` carries everything that changes the structure (see the page): rowMenus(entry),
 * startRename / stopRename / commitName, devicesOf / deviceChange / deleteDevice / addDevices, selection,
 * drag, editingDevice, focusClass, isImported, jump(searchEntry), canEdit, renamingId.
 */
export function StructureEditor({
  structure, selectedId, onSelect, collapsed, onToggle, onExpandAll, ops, searchIndex, onOpenSearch,
  conflicts = [], allowDuplicates, onAllowDuplicatesChange, duplicateSaving, isPhone, onAddBuilding, changes = [], bms = false,
}) {
  const nodes = useMemo(() => (bms ? bmsStructureNodes(structure) : structureNodes(structure)), [structure, bms]);
  const byId = useMemo(() => new Map(nodes.map((entry) => [entry.id, entry])), [nodes]);
  const byParent = useMemo(() => {
    const map = new Map();
    nodes.forEach((entry) => {
      if (!entry.parentId) return;
      if (!map.has(entry.parentId)) map.set(entry.parentId, []);
      map.get(entry.parentId).push(entry);
    });
    return map;
  }, [nodes]);
  const roots = nodes.filter((entry) => !entry.parentId);
  // 08 #4: an amber dot on every container with an unsaved change below it
  const dirtyIds = useMemo(
    () => changedContainerIds(changes, (nodeId) => byId.get(nodeId)?.parentId || null),
    [changes, byId]
  );
  const selected = selectedId ? byId.get(selectedId) || null : null;
  // Roving tab stop (tree keyboard pattern): exactly one tree item is in the Tab order — the one last focused,
  // else the chosen one, else the first — so the tree is reachable with Tab even when nothing is chosen.
  const treeRef = useRef(null);
  const [activeId, setActiveId] = useState(null);
  const visibleIds = useMemo(() => {
    const ids = [];
    const walk = (list) => list.forEach((entry) => {
      ids.push(entry.id);
      const kids = byParent.get(entry.id) || [];
      if (kids.length && !(collapsed[entry.collapseKey] ?? true)) walk(kids);
    });
    walk(nodes.filter((entry) => !entry.parentId));
    return ids;
  }, [nodes, byParent, collapsed]);
  const tabbableId = [activeId, selectedId, visibleIds[0]].find((id) => id && visibleIds.includes(id)) || null;
  const focusItem = (id) => {
    const target = [...(treeRef.current?.querySelectorAll('[role="treeitem"]') || [])].find((node) => node.dataset.structureNode === id);
    if (target) {
      setActiveId(id);
      target.focus();
    }
  };
  const nav = {
    onFocusItem: setActiveId,
    focus: focusItem,
    move: (fromId, key) => {
      const index = visibleIds.indexOf(fromId);
      const next = key === 'Home' ? 0 : key === 'End' ? visibleIds.length - 1 : index + (key === 'ArrowDown' ? 1 : -1);
      if (next >= 0 && next < visibleIds.length) focusItem(visibleIds[next]);
    },
  };
  // choosing a node opens its ancestors in the tree, so the tree always shows where you are
  const select = (entry) => {
    if (entry) {
      const keys = [];
      let parent = entry.parentId ? byId.get(entry.parentId) : null;
      while (parent) {
        keys.push(parent.collapseKey);
        parent = parent.parentId ? byId.get(parent.parentId) : null;
      }
      if (keys.length) onExpandAll(keys);
    }
    onSelect(entry ? entry.id : null);
  };
  const duplicateIds = useMemo(() => new Set(conflicts.flatMap((conflict) => conflict.entries.map((item) => item.id))), [conflicts]);
  const showTree = !isPhone || !selected;
  const showDetail = !isPhone || Boolean(selected);

  return (
    <div className="overflow-hidden rounded-card border border-line bg-surface shadow-card" data-testid="structure-editor">
      <div className={cn('grid min-h-[480px]', !isPhone && 'lg:grid-cols-[340px_minmax(0,1fr)]')}>
        {showTree ? (
          <aside className="border-b border-line bg-surface-2 px-3 py-3.5 lg:border-b-0 lg:border-r" aria-label="Structure tree">
            <StructureSearch index={searchIndex} onOpen={onOpenSearch} className="sm:w-full" />
            <div className="mb-1.5 mt-3 flex items-center justify-between px-1.5">
              <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">{fmt(roots.length)} building{roots.length === 1 ? '' : 's'}</span>
              {roots.length ? (
                <button type="button" className="text-[12px] font-semibold text-brand-ink hover:underline" onClick={() => onExpandAll(nodes.filter((entry) => entry.kind === 'building' || entry.kind === 'floor').map((entry) => entry.collapseKey))}>
                  Expand all
                </button>
              ) : null}
            </div>
            {roots.length ? (
              <ul ref={treeRef} role="tree" aria-label={bms ? 'Buildings, subs and sub-items' : 'Buildings, floors and panels'} className="space-y-0.5" data-testid="structure-tree">
                {roots.map((entry) => (
                  <TreeRow key={entry.id} entry={entry} byParent={byParent} selectedId={selectedId} collapsed={collapsed} onToggle={onToggle} onSelect={select} ops={ops} tabbableId={tabbableId} nav={nav} dirtyIds={dirtyIds} bms={bms} />
                ))}
              </ul>
            ) : (
              <p className="px-2 py-6 text-center text-[13px] text-ink-3">
                {ops.canEdit ? 'No buildings yet. Add one, or Import a file — execution sub-items follow the structure.' : 'No buildings yet.'}
              </p>
            )}
            {ops.canEdit ? (
              <button type="button" onClick={onAddBuilding} className="mt-3 flex h-10 w-full items-center gap-2 rounded-lg border border-dashed border-[color:var(--line-dashed)] px-3 text-[13px] font-semibold text-ink-2 hover:bg-surface-hover">
                <Plus className="h-4 w-4" aria-hidden="true" />
                Add building
              </button>
            ) : null}
            {ops.canEdit ? (
              <p className="mt-3 hidden px-1 text-[11.5px] text-ink-3 lg:block">
                <Layers className="mr-1 inline h-3.5 w-3.5" aria-hidden="true" />
                {bms
                  ? 'F2 or double-click to rename · a sub-item with sub-items of its own opens like a folder'
                  : 'F2 or double-click to rename · drag a device to reorder, or onto a panel to move it'}
              </p>
            ) : bms ? (
              <p className="mt-3 hidden px-1 text-[11.5px] text-ink-3 lg:block">
                <Layers className="mr-1 inline h-3.5 w-3.5" aria-hidden="true" />
                A sub-item with sub-items of its own opens like a folder
              </p>
            ) : null}
          </aside>
        ) : null}
        {showDetail ? (
          <div className="min-w-0 px-4 py-4 sm:px-6 sm:py-5">
            {selected ? (
              <Detail
                entry={selected}
                byParent={byParent}
                byId={byId}
                ops={ops}
                conflicts={conflicts}
                duplicateIds={duplicateIds}
                onSelect={select}
                onBack={() => select(null)}
                isPhone={isPhone}
                allowDuplicates={allowDuplicates}
                onAllowChange={onAllowDuplicatesChange}
                duplicateSaving={duplicateSaving}
                bms={bms}
              />
            ) : (
              <Overview nodes={nodes} conflicts={conflicts} ops={ops} allowDuplicates={allowDuplicates} onAllowChange={onAllowDuplicatesChange} duplicateSaving={duplicateSaving} onSelect={select} onAddBuilding={onAddBuilding} bms={bms} />
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** Address conflicts from the search index (findDuplicateAddresses): [{ address, entries }] by address. */
export function structureConflicts(duplicates) {
  return [...(duplicates || new Map()).entries()]
    .map(([address, entries]) => ({ address, entries }))
    .sort((a, b) => a.address.localeCompare(b.address, undefined, { numeric: true }));
}
